import React, { useState, useEffect, useCallback } from 'react';
import { X, ChevronLeft, ChevronRight, ZoomIn, ZoomOut, RotateCw, Download, Star } from 'lucide-react';

const PhotoModal = ({ photo, onClose, photos = [], currentIndex = 0, onNavigate }) => {
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [imageLoaded, setImageLoaded] = useState(false);

  // Navigation avec les flèches du clavier
  const handleKeyPress = useCallback((e) => {
    switch (e.key) {
      case 'Escape':
        onClose();
        break;
      case 'ArrowLeft':
        if (currentIndex > 0) {
          onNavigate(currentIndex - 1);
        }
        break;
      case 'ArrowRight':
        if (currentIndex < photos.length - 1) {
          onNavigate(currentIndex + 1);
        }
        break;
      case '+':
      case '=':
        handleZoomIn();
        break;
      case '-':
        handleZoomOut();
        break;
      case 'r':
      case 'R':
        handleRotate();
        break;
      default:
        break;
    }
  }, [currentIndex, photos.length, onNavigate, onClose]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyPress);
    document.body.style.overflow = 'hidden'; // Empêcher le scroll de la page

    return () => {
      document.removeEventListener('keydown', handleKeyPress);
      document.body.style.overflow = 'unset';
    };
  }, [handleKeyPress]);

  // Réinitialiser la vue quand on change de photo
  useEffect(() => {
    setZoom(1);
    setRotation(0);
    setPosition({ x: 0, y: 0 });
    setImageLoaded(false);
  }, [photo._id]);

  // Fonctions de manipulation
  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev * 1.2, 3));
  };

  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev / 1.2, 0.5));
  };

  const handleRotate = () => {
    setRotation(prev => (prev + 90) % 360);
  };

  const resetView = () => {
    setZoom(1);
    setRotation(0);
    setPosition({ x: 0, y: 0 });
  };

  // 🔧 CORRECTION: Double-clic pour fermer
  const handleDoubleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onClose();
  };

  // Gestion du drag
  const handleMouseDown = (e) => {
    e.preventDefault();
    if (zoom > 1) {
      setIsDragging(true);
      setDragStart({
        x: e.clientX - position.x,
        y: e.clientY - position.y
      });
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging && zoom > 1) {
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Download de l'image
  const handleDownload = async () => {
    try {
      const response = await fetch(photo.url);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `bijou_${photo._id}.jpg`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erreur lors du téléchargement:', error);
    }
  };

  const canNavigateLeft = currentIndex > 0;
  const canNavigateRight = currentIndex < photos.length - 1;

  return (
    <div 
      className="fixed inset-0 z-50 bg-black bg-opacity-90 flex items-center justify-center"
      onClick={onClose} // 🔧 CORRECTION: Fermer en cliquant sur l'arrière-plan
    >
      {/* Header avec contrôles */}
      <div className="absolute top-0 left-0 right-0 z-10 bg-gradient-to-b from-black/50 to-transparent p-4">
        <div className="flex items-center justify-between text-white">
          <div className="flex items-center space-x-4">
            <h3 className="text-lg font-medium">
              {photo.description || 'Photo du bijou'}
            </h3>
            {photo.isPrimary && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-500 text-yellow-900">
                <Star className="h-3 w-3 mr-1" />
                Principale
              </span>
            )}
          </div>
          
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-300">
              {currentIndex + 1} / {photos.length}
            </span>
          </div>
        </div>
      </div>

      {/* Contrôles de manipulation */}
      <div className="absolute top-16 right-4 z-10 flex flex-col space-y-2">
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleZoomIn();
          }}
          className="p-2 bg-black/50 text-white rounded-lg hover:bg-black/70 transition-colors"
          title="Zoom avant (+)"
        >
          <ZoomIn className="h-5 w-5" />
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleZoomOut();
          }}
          className="p-2 bg-black/50 text-white rounded-lg hover:bg-black/70 transition-colors"
          title="Zoom arrière (-)"
        >
          <ZoomOut className="h-5 w-5" />
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleRotate();
          }}
          className="p-2 bg-black/50 text-white rounded-lg hover:bg-black/70 transition-colors"
          title="Rotation (R)"
        >
          <RotateCw className="h-5 w-5" />
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation();
            resetView();
          }}
          className="p-2 bg-black/50 text-white rounded-lg hover:bg-black/70 transition-colors text-xs"
          title="Réinitialiser la vue"
        >
          1:1
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleDownload();
          }}
          className="p-2 bg-black/50 text-white rounded-lg hover:bg-black/70 transition-colors"
          title="Télécharger"
        >
          <Download className="h-5 w-5" />
        </button>
      </div>

      {/* Navigation gauche */}
      {canNavigateLeft && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onNavigate(currentIndex - 1);
          }}
          className="absolute left-4 top-1/2 transform -translate-y-1/2 z-10 p-3 bg-black/50 text-white rounded-full hover:bg-black/70 transition-colors"
          title="Photo précédente (←)"
        >
          <ChevronLeft className="h-6 w-6" />
        </button>
      )}

      {/* Navigation droite */}
      {canNavigateRight && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onNavigate(currentIndex + 1);
          }}
          className="absolute right-4 top-1/2 transform -translate-y-1/2 z-10 p-3 bg-black/50 text-white rounded-full hover:bg-black/70 transition-colors"
          title="Photo suivante (→)"
        >
          <ChevronRight className="h-6 w-6" />
        </button>
      )}

      {/* Bouton fermer */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        className="absolute top-4 right-4 z-10 p-2 bg-black/50 text-white rounded-full hover:bg-black/70 transition-colors"
        title="Fermer (Echap)"
      >
        <X className="h-6 w-6" />
      </button>

      {/* 🔧 CORRECTION: Conteneur d'image optimisé avec aspect ratio préservé */}
      <div 
        className="relative w-full h-full flex items-center justify-center p-8"
        onClick={(e) => e.stopPropagation()} // Empêcher la fermeture quand on clique sur l'image
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      >
        {/* 🔧 CORRECTION: Image avec dimensions optimisées */}
        <img
          src={photo.url}
          alt={photo.description || 'Photo du bijou'}
          className="max-w-full max-h-full object-contain select-none"
          style={{
            transform: `scale(${zoom}) rotate(${rotation}deg) translate(${position.x / zoom}px, ${position.y / zoom}px)`,
            transformOrigin: 'center center',
            transition: isDragging ? 'none' : 'transform 0.2s ease-out',
            cursor: zoom > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default',
            width: 'auto',
            height: 'auto'
          }}
          draggable={false}
          onLoad={() => setImageLoaded(true)}
          onDoubleClick={handleDoubleClick} // 🔧 CORRECTION: Double-clic sur l'image
        />

        {/* Indicateur de chargement */}
        {!imageLoaded && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
          </div>
        )}
      </div>

      {/* Footer avec informations */}
      <div className="absolute bottom-0 left-0 right-0 z-10 bg-gradient-to-t from-black/50 to-transparent p-4">
        <div className="text-white text-center">
          <p className="text-sm text-gray-300">
            Double-clic pour fermer • Flèches pour naviguer • +/- pour zoomer • R pour rotation
          </p>
          {photo.description && (
            <p className="text-lg mt-1">{photo.description}</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default PhotoModal;